import { z } from 'zod';
import { MulticlassConfigSchema } from './multiclass';
import { SpellLevelSchema } from './spell';
import { StatIdSchema } from './stats';

/**
 * Categorizzazione di una mossa.
 *
 * - **starting**: mossa iniziale di una classe (presa automaticamente al 1° lv)
 * - **advanced_2_5**: mossa avanzata di livello 2-5
 * - **advanced_6_10**: mossa avanzata di livello 6-10
 * - **class_special**: mosse speciali della classe come archetipi, stili di
 *   difesa, istinti — selezioni "una di N" che non rientrano nei pool standard
 * - **base**: mosse base che TUTTI i PG hanno, indipendentemente dalla classe
 *   (taglia e spacca, parlamentare, ecc.)
 * - **system**: mosse di sistema/cornice (ultimo respiro, fine sessione, ecc.)
 * - **common**: Mosse Comuni — disponibili in modo trasversale sotto condizioni
 *   precise (manuale V1, rr. 1781-1822). Includono:
 *     - *Infanzia Complessa* (esattamente al 2° livello, scelta di un secondo
 *       archetipo della propria classe)
 *     - 5 mosse multiclasse a catena (*Apprendista → Adepto → Esperto →
 *       Maestro → Gran Maestro Multiclasse*) disponibili oltre il 10° livello
 *   La condizione di disponibilità (livello esatto, post-10°, ecc.) è
 *   espressa tramite `prerequisites` o gestita dalla logica di level-up.
 */
export const MoveTypeSchema = z.enum([
  'starting',
  'advanced_2_5',
  'advanced_6_10',
  'class_special',
  'base',
  'system',
  'common',
]);
export type MoveType = z.infer<typeof MoveTypeSchema>;

/**
 * Esito di un tiro per una mossa.
 * `text` è il testo descrittivo dell'effetto (markdown).
 */
export const MoveOutcomeSchema = z.object({
  text: z.string().min(1),
});
export type MoveOutcome = z.infer<typeof MoveOutcomeSchema>;

/**
 * Specifica del tiro per una mossa con tiro.
 *
 * - `stat`: caratteristica con cui tirare. Alcune mosse usano "livello da
 *   incantatore" anziché una caratteristica (vedi manuale draft, riga 1200);
 *   in quel caso si usa il valore speciale `'caster_level'`.
 * - `on12plus`: presente solo per mosse che hanno un esito speciale a 12+
 *   (raro ma esiste).
 * - `on10plus`, `on7to9`: obbligatori (sono il pattern dominante PbtA).
 * - `on6minus`: opzionale; spesso il manuale lascia il 6- al GM senza
 *   testo specifico.
 */
export const MoveRollSchema = z.object({
  stat: z.union([StatIdSchema, z.literal('caster_level')]),
  on12plus: MoveOutcomeSchema.optional(),
  on10plus: MoveOutcomeSchema,
  on7to9: MoveOutcomeSchema,
  on6minus: MoveOutcomeSchema.optional(),
});
export type MoveRoll = z.infer<typeof MoveRollSchema>;

/**
 * Una scelta che il giocatore può fare quando prende una mossa con
 * sotto-struttura (es. *Animale Totemico*: aquila/corvo/lupo/orso;
 * *Faro di Devozione*: i suoi pilastri; *Vincoli del Patrono*).
 *
 * Il giocatore tipicamente sceglie K opzioni (vedi `MoveSchema.choiceCount`).
 * La scelta vive nella scheda del PG come array di `id` referenziati.
 */
export const MoveChoiceSchema = z
  .object({
    /** ID stabile per riferirsi alla scelta dal Character. */
    id: z.string().min(1),
    label: z.string().min(1),
    description: z.string().optional(),
    /**
     * **FI-32**: se definito, l'opzione è disponibile **solo** se il PG ha
     * questo archetipo. Negli altri archetipi l'opzione è nascosta dalla UI
     * e filtrata via {@link isMoveChoiceAvailable}.
     *
     * Esempio: la Tradizione Sacra *Natura* di `paladino.ordine-sacro` è
     * disponibile solo se `archetypeId === 'elfo'` (FI-2).
     */
    requiresArchetype: z.string().min(1).optional(),
    /**
     * **FI-32**: se definito, l'opzione è **gratis** (non conta nel
     * `pickCount` del gruppo) per il PG con questo archetipo. Per gli altri
     * archetipi l'opzione resta normalmente disponibile e contata.
     *
     * Esempi:
     * - miglioramento «truce» di `warlock.vincolo-della-lama`:
     *   `freeForArchetype: 'mezzorco'` (Warlock Mezz'orco lo ottiene gratis
     *   in aggiunta ai 2 miglioramenti scelti).
     * - beneficio «immunità ai veleni» di `warlock.contratto`:
     *   `freeForArchetype: 'nano'` (Warlock Nano).
     */
    freeForArchetype: z.string().min(1).optional(),
    /**
     * **FI-8b — D1**: se `true`, l'opzione può essere selezionata **più
     * di una volta** all'interno dello stesso gruppo (la selezione resta
     * un array, contiene l'id duplicato N volte). L'effetto cumulativo
     * della duplicazione è definito dal consumatore (UI / service);
     * lo schema si limita ad ammetterla.
     *
     * Pattern del manuale (V1 r. 4192-4198, *Famiglio* del Mago): «Ha
     * l'Abilità Adepto a Rango 2 **(3 se scegli quest'opzione 2 volte)**».
     * Solo le 4 peculiarità con clausola cumulativa esplicita lo hanno
     * `true` (`adepto-rango-2`, `altra-abilita-rango-1`,
     * `modificatore-piu-2`, `capacita-utile`); le 2 binarie
     * (`parla-linguaggio-comune`, `telepatia`) lo lasciano `false`
     * — duplicare un'opzione binaria non aggiunge effetto e la UI
     * lo impedisce a monte.
     *
     * Default (omesso): non duplicabile. Backward-compatible per tutte
     * le altre mosse del catalogo (Avvelenatore Ladro, Animale Totemico
     * Barbaro, ecc.) dove le scelte sono distinte. Pattern `.optional()`
     * coerente con `requiresArchetype`/`freeForArchetype` (FI-32): solo
     * le opzioni che vogliono il flag lo dichiarano esplicitamente.
     */
    allowDuplicate: z.boolean().optional(),
  })
  /**
   * **FI-32**: `requiresArchetype` e `freeForArchetype` sono mutuamente
   * esclusivi sulla stessa opzione. Il primo significa "disponibile solo
   * per questo archetipo", il secondo "disponibile a tutti ma gratis per
   * questo archetipo": semanticamente non si applicano insieme.
   */
  .refine((c) => !(c.requiresArchetype && c.freeForArchetype), {
    message:
      'una `MoveChoice` non può avere sia `requiresArchetype` sia `freeForArchetype` (semantica incompatibile)',
    path: ['freeForArchetype'],
  });
export type MoveChoice = z.infer<typeof MoveChoiceSchema>;

/**
 * Un gruppo di scelte indipendenti dentro la stessa mossa.
 *
 * **Pattern multi-gruppo (FI-18)**: alcune mosse del manuale richiedono al
 * giocatore di fare PIÙ scelte, ognuna su una lista distinta. Esempio
 * canonico: *Arma Prescelta* del Guerriero (manuale V1, rr. 3418-3463) ha
 * 3 gruppi indipendenti:
 *  - 1 tipologia di arma (1 di 7: spada, ascia, martello, …)
 *  - 1 portata (1 di 3: corta, media, lunga)
 *  - 2 miglioramenti (2 di 10)
 *
 * Lo schema {@link MoveSchema}.choices (singola lista + `choiceCount`)
 * basta per i casi shorthand single-group. Per i 6 casi multi-gruppo
 * confermati nelle 12 classi DWR (Arma Prescelta, Vincolo della Lama,
 * Missione, Contratto, Faro di Devozione, Divinità) usiamo
 * {@link MoveSchema}.choiceGroups con un array di {@link ChoiceGroupSchema}.
 *
 * Il consumer (UI / predicate) NON dovrebbe trattare separatamente i due
 * casi: l'helper {@link getMoveChoiceGroups} ritorna sempre un array di
 * `ChoiceGroup`, adattando lo shorthand single-group automaticamente.
 *
 * @see {@link MoveSchema}.choiceGroups
 * @see {@link getMoveChoiceGroups}
 */
export const ChoiceGroupSchema = z.object({
  /**
   * ID stabile entro la mossa (univoco per gruppo).
   * Esempi: 'tipologia', 'portata', 'miglioramenti', 'aspetto', 'fondamento'.
   */
  id: z.string().min(1).max(50),
  /**
   * Etichetta visibile per la UI (es. 'Tipologia', 'Portata',
   * 'Miglioramenti', 'Aspetto').
   */
  label: z.string().min(1).max(100),
  /**
   * Prompt esteso opzionale (es. "Scegli il Fondamento della tua devozione").
   * Se assente, la UI mostra solo `label`.
   */
  description: z.string().optional(),
  /** Opzioni del gruppo (almeno 2). Riusa lo schema delle scelte semplici. */
  options: z.array(MoveChoiceSchema).min(2),
  /**
   * Quante opzioni il giocatore deve scegliere.
   * Default 1 (radio). Per `> 1` la UI usa checkbox con cap automatico.
   * Esempio: *Arma Prescelta* miglioramenti = `pickCount: 2`.
   */
  pickCount: z.number().int().min(1).default(1),
});
export type ChoiceGroup = z.infer<typeof ChoiceGroupSchema>;

/**
 * Un campo narrativo libero compilato dal giocatore al **setup** di una mossa.
 *
 * **Pattern (FI-18)**: alcune mosse chiedono al giocatore di descrivere o
 * nominare qualcosa al momento della presa, fissando un canone permanente
 * del PG. Esempi:
 *  - *Arma Prescelta* (Guerriero): "Descrivi l'arma" + "Nome dell'arma" (opz)
 *  - *Faro di Devozione* (Paladino): "Nome dell'entità ultraterrena"
 *  - *Nato dalla Terra* (Druido): "Segno Rivelatore"
 *  - *Sentieri del Monaco*: "Nome dell'ordine"
 *  - *Cimelio* (Guerriero L2-5): "Descrivi gli spiriti dentro l'arma"
 *
 * **NON modella le descrizioni runtime** ("descrivi come ti senti dopo la
 * battaglia" ecc.): quelle sono caratterizzazioni di mosse durante il gioco
 * e vivono nella modalità "giocabile" (Fase 2 del progetto), non sulla
 * scheda PG strutturata.
 *
 * **Validazione**: il valore compilato dal giocatore deve essere non-vuoto
 * dopo `trim()` (anche `.` è accettato). Il vincolo è intenzionalmente lasco
 * per non bloccare il PG che vuole posporre la descrizione completa a una
 * sessione successiva.
 */
export const NarrativeFieldSchema = z.object({
  /**
   * ID stabile entro la mossa (univoco per field).
   * Esempi: 'descrizione-arma', 'nome-arma', 'nome-entita',
   * 'segno-rivelatore', 'nome-ordine'.
   */
  id: z.string().min(1).max(50),
  /**
   * Etichetta visibile per la UI (es. "Descrivi l'arma", "Nome dell'entità").
   */
  label: z.string().min(1).max(150),
  /**
   * Placeholder UI / prompt esteso opzionale.
   * Mostrato dentro l'input/textarea quando il campo è vuoto.
   */
  placeholder: z.string().optional(),
  /**
   * Se `true` (default), il campo deve essere compilato (non-vuoto dopo trim)
   * al finalize. Se `false`, è opzionale.
   */
  required: z.boolean().default(true),
  /**
   * Se `true`, il campo va renderizzato come `<textarea>` multi-riga.
   * Se `false` (default), come `<input type="text">` single-line.
   * Convenzione: nomi e identificativi brevi → single-line; descrizioni
   * di oggetti, entità, fenomeni → multiline.
   *
   * Ignorato se {@link NarrativeFieldSchema.oneOf} è definito (il rendering
   * passa a `<Select>`).
   */
  multiline: z.boolean().default(false),
  /**
   * **FI-8b — D1.5**: se definito, il field rappresenta una scelta da un
   * enum chiuso anziché testo libero. La UI lo renderizza come
   * `<Select>` con queste N opzioni. La validazione applicativa è
   * responsabilità del consumer: il valore compilato dev'essere uno
   * tra `oneOf` (non strettamente enforced dallo schema per coerenza con
   * la flessibilità degli altri narrativeField — solo `min 2` opzioni).
   *
   * Esempio: nella mossa `mago.famiglio`, il narrativeField
   * `'quale-modificatore'` ha `oneOf: ['forza','destrezza','costituzione',
   * 'intelligenza','saggezza','carisma']` e attiva quando il giocatore
   * sceglie la peculiarità `modificatore-piu-2`.
   *
   * Etichette leggibili (es. "Forza" per `'forza'`) sono lookup esterno
   * a carico della UI (helper specifici per dominio).
   */
  oneOf: z.array(z.string().min(1)).min(2).optional(),
  /**
   * **FI-8a (esteso in D1.5)**: campo narrativo **condizionale**. Se
   * definito, il field è visibile (e, se `required`, validato) solo quando
   * il `ChoiceGroup` indicato ha selezionato l'opzione `optionId`. Tipici
   * casi d'uso:
   *  - scelta "altro / concorda con il GM" che apre un input testuale per
   *    specificare valore custom (FI-8a, Compagno Animale);
   *  - peculiarità del Follower che richiede prompt di chiarimento (FI-8b,
   *    Famiglio: «quale Abilità», «quale stat», label/desc capacità custom).
   *
   * Quando `requiresChoice` non è soddisfatto, il field è nascosto dalla
   * UI e ignorato da `isMoveSetupComplete` (anche se `required: true`).
   *
   * **D1.5 — `minOccurrences`**: per supportare opzioni con
   * `MoveChoice.allowDuplicate: true` dove ogni occorrenza apre un prompt
   * separato (caso `capacita-utile` del Famiglio, scelto 2× → 2 capacità
   * custom indipendenti). Il field `*-N` (con N occorrenza) è visibile
   * solo se l'opzione è stata selezionata almeno `minOccurrences` volte.
   * Default 1 (presenza singola), retrocompatibile con tutti i field FI-8a.
   */
  requiresChoice: z
    .object({
      groupId: z.string().min(1),
      optionId: z.string().min(1),
      minOccurrences: z.union([z.literal(1), z.literal(2)]).optional(),
    })
    .optional(),
});
export type NarrativeField = z.infer<typeof NarrativeFieldSchema>;

/**
 * Verifica se un `NarrativeField` è **visibile** dato un `MoveRef`. Per
 * field con `requiresChoice`, controlla che l'opzione attiva nel
 * `ChoiceGroup` indicato corrisponda. Per field senza `requiresChoice`,
 * ritorna sempre `true`.
 *
 * **D1.5 — count-aware**: se `requiresChoice.minOccurrences` è definito,
 * il field è visibile solo se l'opzione è stata selezionata almeno
 * `minOccurrences` volte (caso `MoveChoice.allowDuplicate: true`).
 * Senza `minOccurrences` il comportamento è quello FI-8a: presenza
 * singola → visibile.
 *
 * Helper usato sia dal predicate del builder (`isMoveSetupComplete`) sia
 * dal componente UI (`MoveNarrativeFieldsField`) per restare coerenti.
 */
export function isNarrativeFieldVisible(
  field: NarrativeField,
  selectedChoices: readonly string[],
  selectedChoiceGroups: Readonly<Record<string, readonly string[]>> | undefined,
): boolean {
  if (!field.requiresChoice) return true;
  const { groupId, optionId, minOccurrences } = field.requiresChoice;
  // D8a: priorità a `selectedChoiceGroups[groupId]` se popolato (mosse
  // multi-gruppo come Druido Supporto Elementale, che ha un group `main` in
  // `choiceGroups`). Cade su `selectedChoices` shorthand solo se
  // `selectedChoiceGroups[main]` è assente e `groupId === 'main'`.
  const selected = resolveGroupSelections(groupId, selectedChoices, selectedChoiceGroups);
  const occurrences = selected.filter((id) => id === optionId).length;
  return occurrences >= (minOccurrences ?? 1);
}

function resolveGroupSelections(
  groupId: string,
  selectedChoices: readonly string[],
  selectedChoiceGroups: Readonly<Record<string, readonly string[]>> | undefined,
): readonly string[] {
  const fromGroups = selectedChoiceGroups?.[groupId];
  if (fromGroups !== undefined) return fromGroups;
  if (groupId === 'main') return selectedChoices;
  return [];
}

/**
 * Prerequisito atomico per una mossa avanzata. Tre forme:
 *  - **stringa**: ID di una mossa che il PG deve avere (es. `'ladro.attacco-furtivo'`)
 *  - **`{ allOf: [...] }`**: il PG deve avere TUTTE le mosse elencate. Forma
 *    esplicita per i casi a 2+ mosse richieste insieme. (Equivalente a una
 *    lista di stringhe alla vecchia maniera, ma esplicitamente AND.)
 *  - **`{ anyOf: [...] }`**: il PG deve avere ALMENO UNA delle mosse
 *    elencate (OR). Caso del Guerriero "*Cimelio* richiede *Arma Prescelta*
 *    o *Vincolo della Lama*" (manuale V1 r. 3516).
 *
 * **Cross-class**: gli ID nelle alternative possono essere di classi diverse
 * (es. `warlock.vincolo-della-lama` come alternativa a `guerriero.arma-prescelta`).
 * Lo schema NON verifica che la mossa puntata esista (richiederebbe il
 * catalogo cross-class); la verifica è responsabilità di test data-side e
 * di un service futuro.
 */
export const PrerequisiteSchema = z.union([
  z.string().min(1),
  z.object({ allOf: z.array(z.string().min(1)).min(2) }),
  z.object({ anyOf: z.array(z.string().min(1)).min(2) }),
]);
export type Prerequisite = z.infer<typeof PrerequisiteSchema>;

/**
 * Estrae tutti gli ID di mosse referenziate in un `Prerequisite`,
 * indipendentemente dalla sua forma (stringa singola, `allOf`, `anyOf`).
 * Utile per i test di integrità referenziale.
 */
export function getPrerequisiteMoveIds(prereq: Prerequisite): readonly string[] {
  if (typeof prereq === 'string') return [prereq];
  if ('allOf' in prereq) return prereq.allOf;
  return prereq.anyOf;
}

/**
 * **FI-33 (D2)**: sorgente del pool di spell selezionabili per una grant.
 *
 * Discriminated union su `kind`:
 *  - **`'own-class'`**: pool = lista della classe a cui appartiene la
 *    mossa che concede la grant (es. *Tomo del Sapere* del Warlock concede
 *    spell del Warlock; *Corpus Arcano* del Mago concede spell del Mago).
 *  - **`'other-classes'`**: pool = qualsiasi classe DIVERSA dalla propria
 *    (es. *Magnus Corpus Arcano*: «dalla lista di qualsiasi altra classe»).
 *  - **`'any-class'`**: pool = qualsiasi classe inclusa la propria (es.
 *    *Rivelazioni Proibite*: «da qualunque lista di classe (inclusa quella
 *    del Warlock)»).
 */
export const SpellGrantSourceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('own-class') }),
  z.object({ kind: z.literal('other-classes') }),
  z.object({ kind: z.literal('any-class') }),
]);
export type SpellGrantSource = z.infer<typeof SpellGrantSourceSchema>;

/**
 * **FI-33 (D2)** + **FI-35 (D-FI35)**: vincolo sul livello degli spell
 * selezionabili. Discriminated union su `kind`, partizionata in 2 famiglie
 * (vedi helper {@link isAutoSpellGrantLevelRule}):
 *
 * **Famiglia "pick"** (il giocatore sceglie N spell, `SpellGrantSchema.count`
 * richiesto):
 *  - **`'fixed'`**: esattamente quel livello (es. *Tomo del Sapere* "2 di
 *    livello 1 e 2 di livello 3" → 2 grant con `levelRule.kind: 'fixed'`).
 *  - **`'lte-character-level'`**: qualsiasi livello ≤ livello del PG. Pool
 *    elastico (scala automaticamente con il livello PG: a L1 solo L0+L1,
 *    a L3 anche L3, …, a L9 tutto). Caso *Corpus Arcano* (regola Mago) e
 *    *Magnus Corpus Arcano*.
 *  - **`'any-with-cap'`**: qualsiasi livello, opzionalmente con livello
 *    massimo (campo `maxLevel`). Combinabile con `perLevelCap` su
 *    `SpellGrantSchema` per limitare il numero di spell di un certo livello
 *    (es. *Rivelazioni Proibite*: «2 di qualsiasi livello (al massimo uno
 *    solo di 9°)»).
 *
 * **Famiglia "auto"** (le spell sono aggiunte automaticamente, niente scelta
 * del giocatore, `SpellGrantSchema.count` ignorato/omesso):
 *  - **`'all-of-level'`**: aggiunge **tutte** le spell della `source` con
 *    quel livello. Esempi: *Vincolo del Tomo* del Warlock concede tutte le
 *    Suppliche L0 (`{ kind: 'all-of-level', level: 0 }` su source `own-class`);
 *    pattern simile per le tradizione-native del Chierico/Druido/Paladino
 *    (tutte L0+L1 della Tradizione).
 *  - **`'fixed-list'`**: aggiunge una lista esplicita di spell-id. Esempio:
 *    il Mago L1 concede 3 trucchetti specifici (*Luce*, *Prestidigitazione*,
 *    *Servitore Invisibile*) — `{ kind: 'fixed-list', spellIds: [...] }`. La
 *    `source` viene ignorata (gli ID sono espliciti).
 */
export const SpellGrantLevelRuleSchema = z.discriminatedUnion('kind', [
  // Pick rules (count richiesto su SpellGrantSchema)
  z.object({ kind: z.literal('fixed'), level: SpellLevelSchema }),
  z.object({ kind: z.literal('lte-character-level') }),
  z.object({ kind: z.literal('any-with-cap'), maxLevel: SpellLevelSchema.optional() }),
  // Auto rules (count ignorato/omesso)
  z.object({ kind: z.literal('all-of-level'), level: SpellLevelSchema }),
  z.object({
    kind: z.literal('fixed-list'),
    spellIds: z.array(z.string().min(1)).min(1),
  }),
]);
export type SpellGrantLevelRule = z.infer<typeof SpellGrantLevelRuleSchema>;

/**
 * Helper: la `levelRule` è di famiglia "auto" (le spell sono aggiunte
 * automaticamente, niente scelta del giocatore)?
 *
 * Esempi:
 *  - `{ kind: 'all-of-level', level: 0 }` → `true`
 *  - `{ kind: 'fixed-list', spellIds: [...] }` → `true`
 *  - `{ kind: 'fixed', level: 1 }` + `count: 2` → `false`
 *  - `{ kind: 'lte-character-level' }` + `count: 1` → `false`
 *
 * Usato dal flow di acquisizione mossa per decidere se uno step di
 * selezione spell è necessario (auto → applica subito; pick → mostra step).
 */
export function isAutoSpellGrantLevelRule(rule: SpellGrantLevelRule): boolean {
  return rule.kind === 'all-of-level' || rule.kind === 'fixed-list';
}

/**
 * **FI-33 (D2)**: una "concessione di spell" associata a una mossa.
 *
 * Permette di modellare in modo strutturato tre pattern del manuale:
 *  1. **Regola Mago «+1 spell»** (manuale V1 r. 1758, «se hai la mossa
 *     *Corpus Arcano*, aggiungi un incantesimo a quelli che conosci»):
 *     `mago.corpus-arcano.grantsSpells = [{ trigger: 'on-each-level-up',
 *     count: 1, levelRule: 'lte-character-level', source: 'own-class' }]`.
 *  2. **Mosse advanced che concedono spell al momento della presa** (es.
 *     *Magnus Corpus Arcano*, *Tomo del Sapere*, *Rivelazioni Proibite*):
 *     `trigger: 'on-take'`.
 *  3. **Combinazioni multi-grant** (es. *Tomo del Sapere*: 2 spell L1 + 2
 *     spell L3 sono 2 grant separate nello stesso array `grantsSpells`).
 *
 * **Trigger semantica**:
 *  - `'on-take'` (default): la grant si applica una sola volta, al
 *    momento della presa della mossa nel level-up.
 *  - `'on-each-level-up'`: la grant si applica a ogni level-up successivo
 *    del PG, finché la mossa è in `Character.moves`. Usato per la regola
 *    di classe Mago (`mago.corpus-arcano`). Per multiclasse (FI-17, D7),
 *    chi prende *Corpus Arcano* via *Lanciare Incantesimi Arcani* eredita
 *    la regola — pattern SSOT-friendly.
 *
 * **`perLevelCap`**: opzionale, mappa `SpellLevel → numero max` per
 * limitare la composizione interna alla grant. Es. *Rivelazioni Proibite*
 * grant "2 spell any-with-cap any-class" + `perLevelCap: { 9: 1 }` →
 * il giocatore può scegliere al massimo 1 spell di L9 fra i 2 totali.
 */
export const SpellGrantSchema = z
  .object({
    trigger: z.enum(['on-take', 'on-each-level-up']).default('on-take'),
    /**
     * Numero di spell che il giocatore deve scegliere per questa grant.
     * **Richiesto** per `levelRule` di famiglia "pick" (`fixed`,
     * `lte-character-level`, `any-with-cap`). **Ignorato/omesso** per
     * `levelRule` di famiglia "auto" (`all-of-level`, `fixed-list`) —
     * vedi {@link isAutoSpellGrantLevelRule}.
     *
     * Il refine sotto enforce questa regola.
     */
    count: z.number().int().positive().optional(),
    levelRule: SpellGrantLevelRuleSchema,
    source: SpellGrantSourceSchema,
    /**
     * Cap opzionale per-livello sul numero di spell scelti dentro questa
     * stessa grant. Chiavi: SpellLevel come stringa ('0', '1', '3', '5', '7',
     * '9'). Valori: max int positivo.
     */
    perLevelCap: z.record(z.string(), z.number().int().positive()).optional(),
  })
  /**
   * **FI-35 (D-FI35)**: `count` è obbligatorio per `levelRule` di famiglia
   * "pick" e deve essere omesso per quelle di famiglia "auto" (le spell
   * sono aggiunte automaticamente, niente scelta del giocatore).
   */
  .refine(
    (g) => {
      const isAuto = isAutoSpellGrantLevelRule(g.levelRule);
      if (isAuto) return g.count === undefined;
      return g.count !== undefined && g.count > 0;
    },
    {
      message:
        '`count` è obbligatorio per levelRule "pick" (fixed/lte-character-level/any-with-cap) e deve essere omesso per quelle "auto" (all-of-level/fixed-list).',
      path: ['count'],
    },
  );
export type SpellGrant = z.infer<typeof SpellGrantSchema>;

/**
 * **FI-31 (D5)** + **FI-30 (D6)**: sotto-scelta concessa da una mossa
 * avanzata al PG che la prende. Discriminated union su `kind`:
 *
 *  - **`'from-group'`** (FI-31): il pool della sotto-scelta sono le
 *    **opzioni residue** di uno `StartingMoveChoiceGroup` della classe —
 *    cioè le mosse del gruppo che il PG NON ha già in `Character.moves`.
 *    Caso tipico: *Istinti Primordiali* del Barbaro («ottieni un Istinto
 *    che non hai già»); *Formazione Eterogenea* del Mago; le 14 mosse
 *    del pattern `from-group` su 7 classi.
 *
 *  - **`'from-choice-move'`** (FI-31): il pool della sotto-scelta sono le
 *    **choices residue** di una mossa-config — cioè le `MoveChoice` della
 *    `MoveSchema.choices` della mossa-config che il PG non ha già in
 *    `selectedChoices` / `selectedChoiceGroups`. Caso tipico: *Ho Ancora
 *    Fame* del Barbaro (sotto-scelta da `barbaro.appetiti-erculei.choices`);
 *    *Aura Versatile* del Paladino (sotto-scelta da `paladino.aura.choices`);
 *    *Nel Bene o nel Male* dello Sciamano (sotto-scelta da
 *    `sciamano.che-tu-sia.choices`).
 *
 *  - **`'from-choice-mapping'`** (FI-30, D6): la mossa concede una
 *    **mossa cross-class predeterminata**, derivata univocamente da una
 *    scelta strutturata fatta in una mossa-config della stessa classe
 *    (`sourceMoveId.choices`). Il `mapping` è `Record<choiceId, moveId>`:
 *    per ogni id di `MoveChoice` selezionato nel `MoveRef` di
 *    `sourceMoveId`, indica l'id-mossa cross-class concessa. Pattern UNICO
 *    nel manuale V1: ***Essere il Totem*** del Barbaro (advanced_6_10)
 *    concede una mossa di altra classe in base alla specie scelta in
 *    `barbaro.istinto-misticismo.choices` (aquila/corvo/lupo/orso →
 *    Ladro/Guerriero/Paladino/Paladino). Il manuale chiarisce: «Questa
 *    mossa **non si considera** una mossa multiclasse» — pattern distinto
 *    da FI-17 (multiclasse a pool aperto, D7).
 *
 * **Semantica applicativa** (servizio `applyLevelUp`):
 *  - `'from-group'` + `'from-choice-mapping'`: la sotto-scelta selezionata
 *    viene aggiunta come nuova `MoveRef` a `Character.moves` (con eventuale
 *    setup compilato se la mossa target ha `hasMoveSetup`). Entrambe usano
 *    `AcquisitionSource: 'granted'` via `applyMoveAcquisition`.
 *  - `'from-choice-move'`: la sotto-scelta selezionata viene aggiunta come
 *    `selectedChoices` aggiuntivo del `MoveRef` esistente della mossa-config
 *    sorgente in `Character.moves`. NON crea un nuovo `MoveRef`.
 *
 * **Disponibilità della mossa**: una mossa con `grantsMoveChoice` è
 * disponibile al level-up solo se il pool residuo è `≥ 1`. Es: PG con tutti
 * gli Istinti già presi → *Istinti Primordiali* filtrata fuori dal pool
 * delle mosse advanced. Per `'from-choice-mapping'` il pool è
 * deterministicamente di dimensione `0` o `1` (auto-select in UI).
 *
 * **Cardinalità**: una mossa NON può avere più di una grant di sotto-scelta
 * (la sotto-scelta è singolare, a differenza di `grantsSpells` che è array).
 */
export const MoveGrantsMoveChoiceSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('from-group'),
    /** ID dello `StartingMoveChoiceGroup` della classe da cui pescare il pool residuo. */
    groupId: z.string().min(1),
  }),
  z.object({
    kind: z.literal('from-choice-move'),
    /** ID della mossa-config (con `MoveSchema.choices`) da cui pescare il pool residuo. */
    moveId: z.string().min(1),
  }),
  z
    .object({
      /**
       * **FI-82 (2026-05-24)** — Pool inline. La mossa concede una scelta
       * tra le `moveOptions` elencate esplicitamente. A differenza di
       * `from-group` (che legge dal `classDef.startingChoiceGroups` del
       * PG) e `from-choice-mapping` (che mappa la choice di una mossa
       * sorgente), `from-list` è **self-contained** sulla mossa con la
       * grant — nessun lookup esterno, nessuna dipendenza da
       * `classDef`. Adatto a regole tipo "Se prendi questa mossa,
       * ottieni a scelta X o Y" indipendenti dalla classe del PG.
       *
       * Caso pilota (FI-82): `paladino.faro-di-devozione` concede
       * `[paladino.ordine-sacro, paladino.ordine-sovrannaturale]`.
       * Funziona uniformemente al L1 Paladino (Faro è auto in
       * `startingMoves`) e al multiclass (es. Guerriero L2 + Faro via
       * *Compagno d'Armi*) — niente logica condizionale "se multiclass
       * allora..." richiesta.
       *
       * Pool filtering: come `from-group`, filtra le mosse già possedute
       * dal PG (idempotenza). Pool vuoto → grant fail (mossa non
       * selezionabile).
       */
      kind: z.literal('from-list'),
      moveOptions: z.array(z.string().min(1)).min(2),
    })
    .readonly(),
  z
    .object({
      kind: z.literal('from-choice-mapping'),
      /**
       * ID della mossa-config sorgente (con `MoveSchema.choices`).
       * Deve essere presente in `Character.moves` con almeno una choice
       * selezionata, altrimenti il pool risolve a `[]` e la mossa è
       * filtrata fuori dal level-up.
       */
      sourceMoveId: z.string().min(1),
      /**
       * Mappa `id-MoveChoice della sorgente → id-mossa cross-class concessa`.
       * La risoluzione: per ogni `selectedChoice` del MoveRef sorgente,
       * lookup `mapping[selectedChoice]` → mossa concessa. Le mosse target
       * possono essere di **qualunque classe** (lookup globale via
       * `findMoveByIdGlobal`); l'integrità referenziale (che la mossa
       * esista nel catalogo) è verificata a test-time data-side, non
       * dallo schema.
       *
       * Esempio (`barbaro.essere-il-totem`):
       * ```ts
       * {
       *   aquila: 'ladro.io-sparo-per-primo',
       *   corvo: 'guerriero.vedere-rosso',
       *   lupo: 'paladino.attacco-a-seguire',
       *   orso: 'paladino.difensore-devoto',
       * }
       * ```
       */
      mapping: z.record(z.string().min(1), z.string().min(1)),
    })
    .refine((g) => Object.keys(g.mapping).length > 0, {
      message:
        '`mapping` di `from-choice-mapping` non può essere vuoto (almeno 1 coppia choice→mossa).',
      path: ['mapping'],
    }),
]);
export type MoveGrantsMoveChoice = z.infer<typeof MoveGrantsMoveChoiceSchema>;

/**
 * Definizione di una mossa nel catalogo.
 *
 * Le istanze di mosse sulle schede PG sono solo riferimenti per ID
 * (vedi `MoveRefSchema` in character.ts), non duplicano i dati qui.
 */
export const MoveSchema = z
  .object({
    /**
     * ID stabile globalmente univoco.
     * Convenzione: `<scope>.<kebab-name>`.
     * Esempi: `mago.dardo-incantato`, `barbaro.appetiti-erculei`,
     * `base.taglia-e-spacca`, `system.ultimo-respiro`.
     */
    id: z.string().min(1),
    name: z.string().min(1).max(100),
    type: MoveTypeSchema,
    /**
     * Classe a cui appartiene la mossa.
     * Undefined per mosse di tipo `base` o `system` (non legate a una classe).
     */
    classId: z.string().optional(),
    /** Trigger ("Quando..."): condizione che innesca la mossa. */
    trigger: z.string().min(1),
    /** Descrizione completa dell'effetto (markdown). */
    description: z.string().min(1),
    /** Specifica del tiro, se la mossa lo prevede. */
    roll: MoveRollSchema.optional(),
    /** Tag della mossa (es. 'continuato', 'multiclasse'). */
    tags: z.array(z.string()).default([]),
    /**
     * Prerequisiti per poter prendere questa mossa ("Richiede:" nel manuale).
     * Lista di `Prerequisite` (vedi schema): ogni entry può essere una stringa
     * (singola mossa richiesta), `{allOf}` (più mosse insieme) o `{anyOf}`
     * (almeno una mossa tra alternative). La lista intera è AND tra le entry.
     *
     * Retrocompatibile: una stringa `'mossa-id'` è equivalente all'array
     * `[{singola: 'mossa-id'}]` semanticamente. Le mosse trascritte prima
     * dell'introduzione di `PrerequisiteSchema` (Ladro) usano stringhe singole
     * e continuano a funzionare senza modifiche.
     */
    prerequisites: z.array(PrerequisiteSchema).default([]),
    /** ID di mosse che questa rimpiazza ("Sostituisce:"). */
    replaces: z.array(z.string()).default([]),
    /**
     * Opzioni disponibili per mosse con sotto-struttura **single-group**
     * (shorthand). Per il pattern multi-gruppo (FI-18) usare invece
     * {@link MoveSchema}.choiceGroups.
     *
     * Una stessa mossa NON può avere sia `choices` sia `choiceGroups`
     * (refine di mutua esclusività).
     */
    choices: z.array(MoveChoiceSchema).optional(),
    /**
     * Quante opzioni il giocatore deve scegliere.
     * Significativo solo se `choices` è definito; default 1.
     */
    choiceCount: z.number().int().min(1).optional(),
    /**
     * **Multi-gruppo (FI-18)**: array di gruppi di scelte indipendenti.
     * Esempio: *Arma Prescelta* del Guerriero ha 3 gruppi (tipologia, portata,
     * miglioramenti). Mutuamente esclusivo con `choices`/`choiceCount`
     * (shorthand single-group): se servono 2+ gruppi, usare `choiceGroups`.
     *
     * Helper {@link getMoveChoiceGroups} unifica i due casi per i consumer.
     */
    choiceGroups: z.array(ChoiceGroupSchema).optional(),
    /**
     * **Narrative-fields (FI-18)**: campi testo libero compilati al setup
     * della mossa per fissare un canone permanente del PG. Esempi:
     * "Descrivi l'arma" + "Nome arma" per *Arma Prescelta*; "Segno Rivelatore"
     * per *Nato dalla Terra*; "Nome dell'ordine" per *Sentieri del Monaco*.
     *
     * NON modella descrizioni runtime ("descrivi come ti senti", "descrivi
     * cosa rompi", ecc.) che caratterizzano la mossa durante il gioco.
     * Quelle vivono nella modalità "giocabile" (Fase 2 del progetto).
     */
    narrativeFields: z.array(NarrativeFieldSchema).optional(),
    /**
     * **FI-33 (D2)**: array di `SpellGrant` opzionale. La mossa concede
     * al PG l'opzione di aggiungere N spell al `Character.spells` da un
     * pool filtrato. Vedi {@link SpellGrantSchema} per la semantica
     * completa di `trigger` (`'on-take'` vs `'on-each-level-up'`),
     * `source` (own/other/any-class), `levelRule` (fixed/lte/any-with-cap)
     * e `perLevelCap` (cap per-livello).
     *
     * Esempi popolati:
     *  - `mago.corpus-arcano`: 1 grant `on-each-level-up`, count 1,
     *    own-class, lte-character-level → regola Mago «+1 spell ogni
     *    level-up» (manuale V1 r. 1758).
     *  - `mago.magnus-corpus-arcano`: 1 grant `on-take`, count 2,
     *    other-classes, lte-character-level.
     *  - `warlock.tomo-del-sapere`: 2 grant `on-take` (2 spell L1 fixed +
     *    2 spell L3 fixed, entrambi own-class).
     *  - `warlock.rivelazioni-proibite`: 2 grant `on-take` (1 spell L5
     *    fixed own + 2 spell any-with-cap any-class con
     *    `perLevelCap: { '9': 1 }`).
     *
     * Campo additivo, opzionale: la maggior parte delle mosse non concede
     * spell. Niente bump versione.
     */
    grantsSpells: z.array(SpellGrantSchema).optional(),
    /**
     * **FI-31 (D5)** + **FI-30 (D6)**: la mossa, una volta presa al
     * level-up, concede al PG una **sotto-scelta** da un pool della
     * classe (FI-31) o una **mossa cross-class predeterminata** mappata
     * da una choice della classe-source (FI-30). Discriminated union su
     * `kind`:
     *  - `'from-group'` (FI-31): pesca dalle opzioni residue di uno
     *    `StartingMoveChoiceGroup` (es. *Istinti Primordiali* del Barbaro,
     *    *Formazione Eterogenea* del Mago);
     *  - `'from-choice-move'` (FI-31): pesca dalle `choices` residue di
     *    una mossa-config esistente (es. *Ho Ancora Fame* da
     *    `barbaro.appetiti-erculei`).
     *  - `'from-choice-mapping'` (FI-30): mappa la scelta strutturata
     *    in una mossa-config sorgente verso una **mossa di un'altra
     *    classe** (es. *Essere il Totem* del Barbaro: aquila/corvo/lupo/orso
     *    in `barbaro.istinto-misticismo` → mossa di Ladro/Guerriero/Paladino).
     *
     * Vedi {@link MoveGrantsMoveChoiceSchema} per la semantica completa
     * (pool, side-effect su `Character`, filtro disponibilità a pool vuoto).
     *
     * Esempi popolati:
     *  - `barbaro.istinti-primordiali`: `{ kind: 'from-group', groupId: 'istinti' }`
     *  - `barbaro.ho-ancora-fame`: `{ kind: 'from-choice-move', moveId: 'barbaro.appetiti-erculei' }`
     *  - `barbaro.essere-il-totem`: `{ kind: 'from-choice-mapping', sourceMoveId: 'barbaro.istinto-misticismo', mapping: { aquila: 'ladro.io-sparo-per-primo', ... } }`
     *
     * Cardinalità: una mossa ha **al massimo 1** sotto-scelta (singolare,
     * a differenza di `grantsSpells` che è array). Campo additivo,
     * opzionale: niente bump versione.
     */
    grantsMoveChoice: MoveGrantsMoveChoiceSchema.optional(),
    /**
     * **FI-8a**: ID del `FollowerTemplate` associato a questa mossa, se la
     * mossa produce un seguace al momento della presa. Es.
     * `'ranger-compagno-animale'` su `ranger.compagno-animale`. Il template
     * vive in `src/data/followers/<id>.ts` e descrive baseStats + mappatura
     * delle scelte del giocatore (`choiceGroups` / `narrativeFields` di questa
     * mossa) verso i campi del `Follower` istanziato.
     *
     * Campo additivo, opzionale: la mossa può continuare a esistere senza
     * follower (la maggior parte delle mosse). Niente bump versione.
     */
    followerTemplateId: z.string().min(1).optional(),
    /**
     * **FI-17 (D7)**: configurazione multiclasse della mossa. Quando presa
     * al level-up, il PG sceglie una mossa di un'altra classe dal pool
     * descritto dalla configurazione. Vedi {@link MulticlassConfigSchema}
     * per la semantica completa (picks, levelAdjustment, extendsByArchetype/
     * Move, excludesClassesPickedInMoves).
     *
     * **Filtro nativo**: il pool target esclude SEMPRE le mosse con
     * `tags: ['multiclasse']` (manuale V1: «Non prendere mosse multiclasse»).
     *
     * Esempi popolati (D7):
     *  - `guerriero.compagno-d-armi`: `{ picks: { kind: 'fixed-classes',
     *    classes: ['paladino','ranger'] } }`
     *  - `guerriero.combattente-multiclasse`: `{ picks: { kind:
     *    'any-other-class' }, levelAdjustment: { delta: -1 } }`
     *  - `sciamano.amico-degli-spiriti`: `{ picks: { kind: 'fixed-classes',
     *    classes: ['barbaro','druido'] }, extendsByArchetype: [{ archetypeId:
     *    'elfo', extraClasses: ['mago'] }] }`
     *
     * Campo additivo, opzionale: la stragrande maggioranza delle mosse non
     * è multiclasse. Niente bump versione.
     */
    multiclass: MulticlassConfigSchema.optional(),
    /**
     * **FI-17 (D7) — bundle all-inclusive** (manuale V1 r. 1766): «i gruppi
     * iniziali di mosse che dipendono l'una dall'altra funzionano come un
     * pacchetto vacanze *all-inclusive*». Quando la mossa viene presa via
     * multiclasse (e in futuro via altri flow di acquisizione), le mosse
     * elencate qui sono automaticamente aggiunte al PG come bundle, con
     * `source: 'bundle'`.
     *
     * Esempio canonico (popolamento rinviato a D7-bundle, PR separato):
     *  - `chierico.lanciare-incantesimi-divini.bundles = ['chierico.divinita',
     *    'chierico.mana']` — chi prende *Lanciare Incantesimi Divini* via
     *    multiclasse riceve in bundle anche *Divinità* (setup Tradizione)
     *    e *Mana*.
     *  - `mago.lanciare-incantesimi-arcani.bundles = ['mago.corpus-arcano',
     *    'mago.mana']` — chi prende *Lanciare Incantesimi Arcani* via
     *    multiclasse riceve anche *Corpus Arcano* (con grantsSpells «+1
     *    spell» multiclasse-friendly) e *Mana*.
     *
     * **Scope D7 corrente**: campo introdotto nello schema ma popolato vuoto
     * sul catalogo. Il flow di acquisizione bundle (`applyBundle` in
     * `services/multiclass.ts`) vivrà in WIP/PR separato D7-bundle. Schema
     * additivo, paga-una-volta-sola.
     *
     * Campo additivo, opzionale. Niente bump versione.
     */
    bundles: z.array(z.string().min(1)).min(1).optional(),
  })
  /**
   * **FI-18**: una mossa NON può avere contemporaneamente `choices` (shorthand
   * single-group) e `choiceGroups` (multi-gruppo). Se servono 2+ gruppi
   * indipendenti di scelta, migrare TUTTI i gruppi in `choiceGroups`. Per il
   * caso 1-gruppo, lo shorthand `choices` resta valido e l'helper
   * {@link getMoveChoiceGroups} lo adatta automaticamente.
   */
  .refine((m) => !(m.choices && m.choiceGroups), {
    message: 'una mossa non può avere sia `choices` (shorthand) sia `choiceGroups` (multi-gruppo)',
    path: ['choiceGroups'],
  });
export type Move = z.infer<typeof MoveSchema>;

/**
 * Helper unificante: ritorna i `ChoiceGroup` di una mossa indipendentemente
 * dal fatto che usi lo shorthand `choices` (single-group) o il multi-gruppo
 * `choiceGroups`.
 *
 * I consumer (UI, predicate, test) dovrebbero usare questo helper invece di
 * controllare `choices`/`choiceGroups` direttamente, così da gestire
 * uniformemente entrambi i casi.
 *
 * Casi:
 *  - mossa con `choiceGroups` definito → ritorna `move.choiceGroups`
 *  - mossa con solo `choices` definito → ritorna 1 gruppo virtuale `'main'`
 *    con `pickCount: move.choiceCount ?? 1`
 *  - mossa senza scelte → ritorna `[]`
 */
export function getMoveChoiceGroups(move: Move): readonly ChoiceGroup[] {
  if (move.choiceGroups) return move.choiceGroups;
  if (move.choices) {
    return [
      {
        id: 'main',
        label: '',
        options: move.choices,
        pickCount: move.choiceCount ?? 1,
      },
    ];
  }
  return [];
}

/**
 * Helper: una mossa ha un setup strutturato che richiede compilazione dal
 * giocatore al momento della presa? Vero se ha `choices`, `choiceGroups`,
 * o `narrativeFields`.
 *
 * Utile per la UI per decidere se mostrare il pannello di setup della mossa.
 */
export function hasMoveSetup(move: Move): boolean {
  return (
    move.choices !== undefined ||
    move.choiceGroups !== undefined ||
    move.narrativeFields !== undefined
  );
}
